/* ──────────────────────────────────────────────────────────────────────
 *  Content Templates — Unified template definitions for the Content system
 *
 *  This file is the single source of truth for all content template types.
 *  It merges and replaces:
 *    - src/Data/Chronicles/chronicleTypes.ts   (pillar groups, type taxonomy)
 *    - src/Data/Chronicles/chronicleTemplates.ts (editor section config)
 *    - src/Data/Blog/blogTypes.ts              (field requirements, editor features)
 *
 *  Architecture reference: docs/architecture/content-architecture-spec.md
 *
 *  The 8 content templates (§3.3):
 *    Topic Tracker, How-To, Mini-Project, Project Chronicle,
 *    Lesson Learned, Best Practice, Professional News, Scholarly Article
 *
 *  Blog types (opinion, personal) are NOT included here — they live on
 *  the user profile as Blogs (§3.2) and are defined in blogTypes.ts.
 *
 *  Adding a new template:
 *    1. Add a value to ContentTemplateKey
 *    2. Add a ContentTemplateDefinition to CONTENT_TEMPLATES
 *    3. Add locale keys under content.templates.<key>.*
 *    4. Add bookshelf visual config (spine color, icon, label)
 *    5. Run: node scripts/add-i18n-key.cjs content templates.<key>.name "…"
 *    6. Run: node scripts/check-i18n-sync.cjs
 * ────────────────────────────────────────────────────────────────────── */

import i18n from '../../i18n/config';

const t = i18n.t.bind(i18n) as (key: string) => string;

// ═══════════════════════════════════════════════════════════════════════
// Template Keys
// ═══════════════════════════════════════════════════════════════════════

export const CONTENT_TEMPLATE_KEYS = [
  'journal_entry',
  'topic_tracker',
  'how_to',
  'mini_project',
  'project_chronicle',
  'lesson_learned',
  'best_practice',
  'professional_news',
  'scholarly_article',
] as const;

export type ContentTemplateKey = (typeof CONTENT_TEMPLATE_KEYS)[number];

// ═══════════════════════════════════════════════════════════════════════
// Trust Tiers (§3.6)
// ═══════════════════════════════════════════════════════════════════════

export const TRUST_TIERS = [
  'early_contributor',
  'contributor',
  'demonstrated_quality',
  'high_trust',
] as const;

export type TrustTier = (typeof TRUST_TIERS)[number];

/** Display order index — lower is less privileged. */
export const TRUST_TIER_ORDER: Record<TrustTier, number> = {
  early_contributor: 0,
  contributor: 1,
  demonstrated_quality: 2,
  high_trust: 3,
};

// ═══════════════════════════════════════════════════════════════════════
// Review Models (§3.3)
// ═══════════════════════════════════════════════════════════════════════

export type ReviewModel = 'light' | 'standard' | 'editorial' | 'scholarly';

// ═══════════════════════════════════════════════════════════════════════
// Bookshelf Visual Config (§5.1)
// ═══════════════════════════════════════════════════════════════════════

export interface BookshelfConfig {
  /** CSS custom property for the spine accent color */
  spineColorToken: string;
  /** Icon identifier (emoji for now; swap to icon component key later) */
  icon: string;
  /** Short label rendered vertically on the spine (ALL CAPS, ≤10 chars) */
  spineLabel: string;
}

// ═══════════════════════════════════════════════════════════════════════
// Editor Features
// ═══════════════════════════════════════════════════════════════════════

export interface EditorFeatures {
  /** Rich text basics: bold, italic, underline, headings */
  richText: boolean;
  /** Ordered & unordered lists */
  lists: boolean;
  /** Inline and block quotes */
  blockquote: boolean;
  /** Hyperlinks */
  links: boolean;
  /** Inline images / hero image upload */
  images: boolean;
  /** Footnotes / endnotes */
  footnotes: boolean;
  /** Structured citations / bibliography */
  citations: boolean;
  /** Pull quotes / callout blocks */
  pullQuotes: boolean;
  /** Data tables (rows, columns, headers) */
  tables: boolean;
  /** Dateline field (city, date) for news articles */
  dateline: boolean;
  /** Source attribution / "Sources:" section */
  sourceAttribution: boolean;
}

// ═══════════════════════════════════════════════════════════════════════
// Template Field Requirements
// ═══════════════════════════════════════════════════════════════════════

export type FieldRequirement = 'required' | 'optional' | 'hidden';

/**
 * Comprehensive field requirements for all content templates.
 *
 * Every template specifies required/optional/hidden for every field.
 * Fields marked 'hidden' are not shown in the editor for that template.
 *
 * The union of all fields across all templates. Template-specific fields
 * (e.g. processSteps for How-To) are 'hidden' on templates that don't
 * use them.
 */
export interface TemplateFields {
  // ── Common fields (most templates use these) ─────────────────────
  headline: FieldRequirement;
  subtitle: FieldRequirement;
  byline: FieldRequirement;
  body: FieldRequirement;
  heroImage: FieldRequirement;
  tags: FieldRequirement;
  category: FieldRequirement;
  dimensionTags: FieldRequirement;

  // ── News-specific ────────────────────────────────────────────────
  dateline: FieldRequirement;
  lede: FieldRequirement;
  sources: FieldRequirement;
  opinionDisclaimer: FieldRequirement;

  // ── Scholarly-specific ───────────────────────────────────────────
  abstract: FieldRequirement;
  keywords: FieldRequirement;
  bibliography: FieldRequirement;
  footnotes: FieldRequirement;
  methodology: FieldRequirement;
  acknowledgments: FieldRequirement;

  // ── How-To structured fields ─────────────────────────────────────
  difficulty: FieldRequirement;
  materialsChecklist: FieldRequirement;
  processSteps: FieldRequirement;
  estimatedCost: FieldRequirement;
  estimatedDuration: FieldRequirement;
  yield: FieldRequirement;
  prerequisites: FieldRequirement;
  safetyWarnings: FieldRequirement;
  variations: FieldRequirement;
  scaleNotes: FieldRequirement;

  // ── Best Practice structured fields ──────────────────────────────
  evidenceMetrics: FieldRequirement;
  applicabilityContext: FieldRequirement;
  successConditions: FieldRequirement;
  failureConditions: FieldRequirement;
  adoptionDifficulty: FieldRequirement;
  timeToResults: FieldRequirement;
  costBenefitSummary: FieldRequirement;
  caseReferences: FieldRequirement;
  recommendations: FieldRequirement;
  beforeAfter: FieldRequirement;

  // ── Lesson Learned structured fields ─────────────────────────────
  expectedOutcome: FieldRequirement;
  actualOutcome: FieldRequirement;
  rootCauseAnalysis: FieldRequirement;
  whatWorked: FieldRequirement;
  whatDidNotWork: FieldRequirement;
  audienceTags: FieldRequirement;
  severityRating: FieldRequirement;
  discoveryTimeline: FieldRequirement;

  // ── Project Chronicle structured fields ──────────────────────────
  teamCredits: FieldRequirement;
  timelineEvents: FieldRequirement;
  outcomeMetrics: FieldRequirement;
  externalLinks: FieldRequirement;
  media: FieldRequirement;
  geography: FieldRequirement;
  outcomeAssessment: FieldRequirement;

  // ── Mini-Project structured fields ───────────────────────────────
  whatWasDone: FieldRequirement;
  howItWasDone: FieldRequirement;
  outcome: FieldRequirement;
  photos: FieldRequirement;
  duration: FieldRequirement;
  linkedHowToGuid: FieldRequirement;

  // ── Cross-template optional fields ───────────────────────────────
  projectAssociation: FieldRequirement;
  phaseContext: FieldRequirement;
  outcomeDescription: FieldRequirement;

  // ── Topic Tracker structured fields ──────────────────────────────
  trackedLinks: FieldRequirement;
  updateEntries: FieldRequirement;
}

// ═══════════════════════════════════════════════════════════════════════
// Editor Tab / Section Config (for complex multi-tab editors)
// ═══════════════════════════════════════════════════════════════════════

export type SectionVisibility = 'visible' | 'hidden' | 'readonly';

export interface SectionConfig {
  visibility: SectionVisibility;
  labelKey?: string;
  hintKey?: string;
}

export interface TabConfig {
  key: string;
  labelKey: string;
  sections: string[];
}

export interface EditorLayout {
  /** Multi-tab layout. If undefined, use single-body editor. */
  tabs?: TabConfig[];
  /** Section-level overrides keyed by section name. */
  sections?: Record<string, SectionConfig>;
}

// ═══════════════════════════════════════════════════════════════════════
// Pillar Linking (§3.4)
// ═══════════════════════════════════════════════════════════════════════

export const PILLAR_TYPES = [
  'problem',
  'solution',
  'enabler',
  'community',
  'project',
] as const;

export type PillarType = (typeof PILLAR_TYPES)[number];

// ═══════════════════════════════════════════════════════════════════════
// Content-to-Content Relationship Types (§3.5)
// ═══════════════════════════════════════════════════════════════════════

export const CONTENT_RELATIONSHIP_TYPES = [
  'methodology',
  'reference',
  'follow_up',
  'related',
] as const;

export type ContentRelationshipType = (typeof CONTENT_RELATIONSHIP_TYPES)[number];

// ═══════════════════════════════════════════════════════════════════════
// Full Template Definition
// ═══════════════════════════════════════════════════════════════════════

export interface ContentTemplateDefinition {
  key: ContentTemplateKey;
  /** i18n key prefix: content.templates.<key> */
  i18nPrefix: string;
  /** Trust tier required to create content with this template */
  trustTier: TrustTier;
  /** Review pipeline model */
  reviewModel: ReviewModel;
  /** Bookshelf UI visual config (§5) */
  bookshelf: BookshelfConfig;
  /** Which TipTap editor extensions to enable */
  editorFeatures: EditorFeatures;
  /** Field-level requirements for the editor */
  templateFields: TemplateFields;
  /** Multi-tab editor layout (Project Chronicle, Topic Tracker). Undefined = single-body. */
  editorLayout?: EditorLayout;
  /** Minimum word count for body (0 = no minimum) */
  minWordCount: number;
  /** Maximum word count (0 = unlimited) */
  maxWordCount: number;
  /** Short description of what this template is for (i18n key) */
  descriptionKey: string;
  /** Whether this template can link to pillar entities */
  supportsPillarLinks: boolean;
  /** Which content relationship types this template can create */
  allowedRelationships: ContentRelationshipType[];
}

// ═══════════════════════════════════════════════════════════════════════
// Section Keys (carried forward from chronicleTemplates.ts)
// ═══════════════════════════════════════════════════════════════════════

export const SECTIONS = {
  OWNERSHIP: 'ownership',
  DIMENSIONS: 'dimensions',
  LOCATION: 'location',
  NARRATIVE: 'narrative',
  BACKGROUND: 'background',
  OUTCOME_ASSESS: 'outcomeAssessment',
  TEAM: 'team',
  TIMELINE: 'timeline',
  LINKS: 'links',
  LINKS_REFERENCE: 'linksReference',
  LINKS_NEWS: 'linksNews',
  METRICS: 'metrics',
} as const;

// ═══════════════════════════════════════════════════════════════════════
// Hidden-all field defaults (DRY helper)
// ═══════════════════════════════════════════════════════════════════════

/** All fields set to 'hidden'. Templates override only what they need. */
const ALL_HIDDEN: TemplateFields = {
  headline: 'hidden',
  subtitle: 'hidden',
  byline: 'hidden',
  body: 'hidden',
  heroImage: 'hidden',
  tags: 'hidden',
  category: 'hidden',
  dimensionTags: 'hidden',
  dateline: 'hidden',
  lede: 'hidden',
  sources: 'hidden',
  opinionDisclaimer: 'hidden',
  abstract: 'hidden',
  keywords: 'hidden',
  bibliography: 'hidden',
  footnotes: 'hidden',
  methodology: 'hidden',
  acknowledgments: 'hidden',
  difficulty: 'hidden',
  materialsChecklist: 'hidden',
  processSteps: 'hidden',
  estimatedCost: 'hidden',
  estimatedDuration: 'hidden',
  yield: 'hidden',
  prerequisites: 'hidden',
  safetyWarnings: 'hidden',
  variations: 'hidden',
  scaleNotes: 'hidden',
  evidenceMetrics: 'hidden',
  applicabilityContext: 'hidden',
  successConditions: 'hidden',
  failureConditions: 'hidden',
  adoptionDifficulty: 'hidden',
  timeToResults: 'hidden',
  costBenefitSummary: 'hidden',
  caseReferences: 'hidden',
  recommendations: 'hidden',
  beforeAfter: 'hidden',
  expectedOutcome: 'hidden',
  actualOutcome: 'hidden',
  rootCauseAnalysis: 'hidden',
  whatWorked: 'hidden',
  whatDidNotWork: 'hidden',
  audienceTags: 'hidden',
  severityRating: 'hidden',
  discoveryTimeline: 'hidden',
  teamCredits: 'hidden',
  timelineEvents: 'hidden',
  outcomeMetrics: 'hidden',
  externalLinks: 'hidden',
  media: 'hidden',
  geography: 'hidden',
  outcomeAssessment: 'hidden',
  whatWasDone: 'hidden',
  howItWasDone: 'hidden',
  outcome: 'hidden',
  photos: 'hidden',
  duration: 'hidden',
  linkedHowToGuid: 'hidden',
  projectAssociation: 'hidden',
  phaseContext: 'hidden',
  outcomeDescription: 'hidden',
  trackedLinks: 'hidden',
  updateEntries: 'hidden',
};

// ═══════════════════════════════════════════════════════════════════════
// Template Definitions
// ═══════════════════════════════════════════════════════════════════════

export const CONTENT_TEMPLATES: Record<ContentTemplateKey, ContentTemplateDefinition> = {

  /* ──────────────────────────────────────────────────────────────────
   *  Journal Entry — the personal blog (DEV-1381, SPEC_PERSONAL_JOURNAL).
   *  Trust: Early Contributor | Review: Light (never enters the review pool)
   *
   *  NAMING (Skip ruling 2026-07-12, SPEC §12.2): `journal_entry` is the
   *  INTERNAL template_key only. Every user-facing surface says "Blog" —
   *  the bare word "Journal" belongs to the *Project* Journal (DEV-1295)
   *  and must never appear on the profile. Hence spineLabel: 'BLOG'.
   *
   *  Subsumes the legacy `opinion` / `personal` blog keys (see LEGACY_KEY_MAP).
   *  Chronicle-backed (OwnerType='user') — no new table. Private by default,
   *  enforced server-side on create (ChroniclesController).
   * ────────────────────────────────────────────────────────────────── */
  journal_entry: {
    key: 'journal_entry',
    i18nPrefix: 'content:templates.journal_entry',
    trustTier: 'early_contributor',
    reviewModel: 'light',
    bookshelf: {
      // Teal, not the spec's first-choice amber: amber (#f59e0b/#d97706) is
      // byte-identical to --color-warning. SPEC §7 names teal as the fallback.
      spineColorToken: 'var(--content-spine-journal)',
      icon: '📓',
      spineLabel: 'BLOG',
    },
    editorFeatures: {
      richText: true,
      lists: true,
      blockquote: true,
      links: true,
      images: true,
      footnotes: false,
      citations: false,
      pullQuotes: true,
      tables: false,
      dateline: false,
      sourceAttribution: false,
    },
    templateFields: {
      ...ALL_HIDDEN,
      headline: 'required',
      body: 'required',
      heroImage: 'optional',
      tags: 'optional',
    },
    // Journaling is not gated by length — no floor, no ceiling.
    minWordCount: 0,
    maxWordCount: 0,
    descriptionKey: 'content:templates.journal_entry.description',
    supportsPillarLinks: false,
    allowedRelationships: ['related'],
  },

  /* ──────────────────────────────────────────────────────────────────
   *  Topic Tracker — Ongoing research notebook on a subject.
   *  Trust: Early Contributor | Review: Light
   *  Spec §3.3: "A living research notebook — curated links, notes,
   *  and updates over time on a topic."
   * ────────────────────────────────────────────────────────────────── */
  topic_tracker: {
    key: 'topic_tracker',
    i18nPrefix: 'content:templates.topic_tracker',
    trustTier: 'early_contributor',
    reviewModel: 'light',
    bookshelf: {
      spineColorToken: 'var(--content-spine-tracker)',
      icon: '📡',
      spineLabel: 'TOPIC TRACKER',
    },
    editorFeatures: {
      richText: true,
      lists: true,
      blockquote: true,
      links: true,
      images: true,
      footnotes: false,
      citations: false,
      pullQuotes: false,
      tables: false,
      dateline: false,
      sourceAttribution: true,
    },
    templateFields: {
      ...ALL_HIDDEN,
      headline: 'required',
      subtitle: 'optional',
      body: 'required',
      heroImage: 'optional',
      tags: 'optional',
      trackedLinks: 'required',
      updateEntries: 'required',
    },
    editorLayout: {
      tabs: [
        {
          key: 'overview',
          labelKey: 'content:editor.tabSetup',
          sections: [SECTIONS.OWNERSHIP, SECTIONS.DIMENSIONS, SECTIONS.LOCATION],
        },
        {
          key: 'topic',
          labelKey: 'content:editor.tabTopicOverview',
          sections: [SECTIONS.NARRATIVE, SECTIONS.BACKGROUND],
        },
        {
          key: 'sources',
          labelKey: 'content:editor.tabSources',
          sections: [SECTIONS.LINKS_REFERENCE, SECTIONS.LINKS_NEWS],
        },
      ],
      sections: {
        [SECTIONS.NARRATIVE]: {
          visibility: 'visible',
          labelKey: 'content:editor.topicOverviewLabel',
          hintKey: 'content:editor.topicOverviewHint',
        },
        [SECTIONS.BACKGROUND]: {
          visibility: 'visible',
          labelKey: 'content:editor.whyThisMattersLabel',
          hintKey: 'content:editor.whyThisMattersHint',
        },
        [SECTIONS.LINKS_REFERENCE]: {
          visibility: 'visible',
          labelKey: 'content:editor.curatedSourcesLabel',
          hintKey: 'content:editor.curatedSourcesHint',
        },
        [SECTIONS.LINKS_NEWS]: {
          visibility: 'visible',
          labelKey: 'content:editor.newsArticlesLabel',
          hintKey: 'content:editor.newsArticlesHint',
        },
        [SECTIONS.OUTCOME_ASSESS]: { visibility: 'hidden' },
        [SECTIONS.METRICS]: { visibility: 'hidden' },
        [SECTIONS.TEAM]: { visibility: 'hidden' },
        [SECTIONS.TIMELINE]: { visibility: 'hidden' },
      },
    },
    minWordCount: 0,
    maxWordCount: 0,
    descriptionKey: 'content:templates.topic_tracker.description',
    supportsPillarLinks: true,
    allowedRelationships: ['reference', 'related'],
  },

  /* ──────────────────────────────────────────────────────────────────
   *  How-To — Procedural guide with steps, materials, difficulty.
   *  Trust: Early Contributor | Review: Standard
   *  Spec §3.3: "Teaches someone how to do something."
   *  Spec §3.8: Reusable instructions across the platform.
   * ────────────────────────────────────────────────────────────────── */
  how_to: {
    key: 'how_to',
    i18nPrefix: 'content:templates.how_to',
    trustTier: 'early_contributor',
    reviewModel: 'standard',
    bookshelf: {
      spineColorToken: 'var(--content-spine-howto)',
      icon: '🔧',
      spineLabel: 'HOW-TO',
    },
    editorFeatures: {
      richText: true,
      lists: true,
      blockquote: false,
      links: true,
      images: true,
      footnotes: false,
      citations: false,
      pullQuotes: false,
      tables: true,
      dateline: false,
      sourceAttribution: true,
    },
    templateFields: {
      ...ALL_HIDDEN,
      headline: 'required',
      subtitle: 'optional',
      body: 'required',
      heroImage: 'optional',
      tags: 'optional',
      category: 'required',
      dimensionTags: 'required',
      lede: 'optional',
      sources: 'optional',
      acknowledgments: 'optional',
      difficulty: 'required',
      materialsChecklist: 'required',
      processSteps: 'required',
      estimatedCost: 'optional',
      estimatedDuration: 'required',
      yield: 'optional',
      prerequisites: 'optional',
      safetyWarnings: 'optional',
      variations: 'optional',
      scaleNotes: 'optional',
      beforeAfter: 'optional',
      recommendations: 'optional',
      projectAssociation: 'optional',
    },
    minWordCount: 200,
    maxWordCount: 10000,
    descriptionKey: 'content:templates.how_to.description',
    supportsPillarLinks: true,
    allowedRelationships: ['reference', 'related'],
  },

  /* ──────────────────────────────────────────────────────────────────
   *  Mini-Project — Lightweight personal project documentation.
   *  Trust: Early Contributor | Review: Standard
   *  Spec §3.3: "Simple structure: what was done, how it was done,
   *  what the outcome was, photos."
   *  Spec §3.7: Tier 1 of the three-tier project model.
   * ────────────────────────────────────────────────────────────────── */
  mini_project: {
    key: 'mini_project',
    i18nPrefix: 'content:templates.mini_project',
    trustTier: 'early_contributor',
    reviewModel: 'standard',
    bookshelf: {
      spineColorToken: 'var(--content-spine-mini)',
      icon: '🌱',
      spineLabel: 'MINI PROJECT',
    },
    editorFeatures: {
      richText: true,
      lists: true,
      blockquote: false,
      links: true,
      images: true,
      footnotes: false,
      citations: false,
      pullQuotes: false,
      tables: false,
      dateline: false,
      sourceAttribution: false,
    },
    templateFields: {
      ...ALL_HIDDEN,
      headline: 'required',
      subtitle: 'optional',
      body: 'optional',
      heroImage: 'optional',
      tags: 'optional',
      category: 'optional',
      dimensionTags: 'optional',
      whatWasDone: 'required',
      howItWasDone: 'required',
      outcome: 'required',
      photos: 'optional',
      duration: 'optional',
      geography: 'optional',
      linkedHowToGuid: 'optional',
      projectAssociation: 'optional',
    },
    minWordCount: 100,
    maxWordCount: 5000,
    descriptionKey: 'content:templates.mini_project.description',
    supportsPillarLinks: true,
    allowedRelationships: ['methodology', 'reference', 'related'],
  },

  /* ──────────────────────────────────────────────────────────────────
   *  Project Chronicle — Structured documentation of a larger project.
   *  Trust: Contributor | Review: Standard
   *  Spec §3.3: "Team credits, timeline events, outcome metrics,
   *  external links, media gallery, geo-tagging."
   *
   *  This template preserves the full Chronicle data model and the
   *  multi-tab editor layout from the original ChronicleEditorPage.
   * ────────────────────────────────────────────────────────────────── */
  project_chronicle: {
    key: 'project_chronicle',
    i18nPrefix: 'content:templates.project_chronicle',
    trustTier: 'contributor',
    reviewModel: 'standard',
    bookshelf: {
      spineColorToken: 'var(--content-spine-chronicle)',
      icon: '📜',
      spineLabel: 'PROJECT CHRONICLE',
    },
    editorFeatures: {
      richText: true,
      lists: true,
      blockquote: true,
      links: true,
      images: true,
      footnotes: false,
      citations: false,
      pullQuotes: true,
      tables: false,
      dateline: false,
      sourceAttribution: true,
    },
    templateFields: {
      ...ALL_HIDDEN,
      headline: 'required',
      subtitle: 'optional',
      body: 'required',
      heroImage: 'optional',
      tags: 'optional',
      category: 'required',
      teamCredits: 'optional',
      timelineEvents: 'optional',
      outcomeMetrics: 'optional',
      externalLinks: 'optional',
      media: 'optional',
      geography: 'optional',
      outcomeAssessment: 'optional',
      projectAssociation: 'optional',
    },
    editorLayout: {
      tabs: [
        {
          key: 'overview',
          labelKey: 'content:editor.tabSetup',
          sections: [SECTIONS.OWNERSHIP, SECTIONS.DIMENSIONS, SECTIONS.LOCATION],
        },
        {
          key: 'story',
          labelKey: 'content:editor.tabStory',
          sections: [SECTIONS.NARRATIVE, SECTIONS.BACKGROUND, SECTIONS.OUTCOME_ASSESS],
        },
        {
          key: 'team',
          labelKey: 'content:editor.tabTeam',
          sections: [SECTIONS.TEAM, SECTIONS.TIMELINE],
        },
        {
          key: 'evidence',
          labelKey: 'content:editor.tabEvidence',
          sections: [SECTIONS.LINKS, SECTIONS.METRICS],
        },
      ],
      sections: {},
    },
    minWordCount: 200,
    maxWordCount: 0,
    descriptionKey: 'content:templates.project_chronicle.description',
    supportsPillarLinks: true,
    allowedRelationships: ['methodology', 'reference', 'follow_up', 'related'],
  },

  /* ──────────────────────────────────────────────────────────────────
   *  Lesson Learned — Reflective analysis tied to real outcomes.
   *  Trust: Demonstrated Quality | Review: Editorial
   *  Spec §3.3: "Root cause analysis, what worked, what didn't,
   *  recommendations."
   * ────────────────────────────────────────────────────────────────── */
  lesson_learned: {
    key: 'lesson_learned',
    i18nPrefix: 'content:templates.lesson_learned',
    trustTier: 'demonstrated_quality',
    reviewModel: 'editorial',
    bookshelf: {
      spineColorToken: 'var(--content-spine-lesson)',
      icon: '💡',
      spineLabel: 'LESSON LEARNED',
    },
    editorFeatures: {
      richText: true,
      lists: true,
      blockquote: true,
      links: true,
      images: true,
      footnotes: false,
      citations: false,
      pullQuotes: true,
      tables: false,
      dateline: false,
      sourceAttribution: false,
    },
    templateFields: {
      ...ALL_HIDDEN,
      headline: 'required',
      subtitle: 'optional',
      body: 'required',
      heroImage: 'optional',
      tags: 'optional',
      category: 'required',
      dimensionTags: 'required',
      lede: 'optional',
      acknowledgments: 'optional',
      beforeAfter: 'optional',
      projectAssociation: 'required',
      phaseContext: 'optional',
      outcomeDescription: 'required',
      recommendations: 'required',
      expectedOutcome: 'required',
      actualOutcome: 'required',
      rootCauseAnalysis: 'required',
      whatWorked: 'required',
      whatDidNotWork: 'required',
      audienceTags: 'optional',
      severityRating: 'required',
      discoveryTimeline: 'optional',
    },
    minWordCount: 300,
    maxWordCount: 5000,
    descriptionKey: 'content:templates.lesson_learned.description',
    supportsPillarLinks: true,
    allowedRelationships: ['reference', 'follow_up', 'related'],
  },

  /* ──────────────────────────────────────────────────────────────────
   *  Best Practice — Prescriptive guidance backed by evidence.
   *  Trust: Demonstrated Quality | Review: Editorial
   *  Spec §3.3: "Prescriptive guidance backed by evidence and case
   *  references."
   * ────────────────────────────────────────────────────────────────── */
  best_practice: {
    key: 'best_practice',
    i18nPrefix: 'content:templates.best_practice',
    trustTier: 'demonstrated_quality',
    reviewModel: 'editorial',
    bookshelf: {
      spineColorToken: 'var(--content-spine-practice)',
      icon: '⭐',
      spineLabel: 'BEST PRACTICE',
    },
    editorFeatures: {
      richText: true,
      lists: true,
      blockquote: true,
      links: true,
      images: true,
      footnotes: false,
      citations: true,
      pullQuotes: true,
      tables: true,
      dateline: false,
      sourceAttribution: true,
    },
    templateFields: {
      ...ALL_HIDDEN,
      headline: 'required',
      subtitle: 'optional',
      body: 'required',
      heroImage: 'optional',
      tags: 'optional',
      category: 'required',
      dimensionTags: 'required',
      lede: 'optional',
      abstract: 'optional',
      sources: 'required',
      bibliography: 'optional',
      acknowledgments: 'optional',
      caseReferences: 'required',
      beforeAfter: 'optional',
      recommendations: 'required',
      projectAssociation: 'optional',
      evidenceMetrics: 'required',
      applicabilityContext: 'required',
      successConditions: 'required',
      failureConditions: 'optional',
      adoptionDifficulty: 'required',
      timeToResults: 'optional',
      costBenefitSummary: 'optional',
      audienceTags: 'optional',
    },
    minWordCount: 500,
    maxWordCount: 8000,
    descriptionKey: 'content:templates.best_practice.description',
    supportsPillarLinks: true,
    allowedRelationships: ['reference', 'related'],
  },

  /* ──────────────────────────────────────────────────────────────────
   *  Professional News — Fact-based reporting with mandatory citations.
   *  Trust: High Trust | Review: Editorial + fact-check
   *  Spec §3.3: "Surfaces on community news pages. Carries the
   *  community's credibility."
   * ────────────────────────────────────────────────────────────────── */
  professional_news: {
    key: 'professional_news',
    i18nPrefix: 'content:templates.professional_news',
    trustTier: 'high_trust',
    reviewModel: 'editorial',
    bookshelf: {
      spineColorToken: 'var(--content-spine-news)',
      icon: '📰',
      spineLabel: 'NEWS',
    },
    editorFeatures: {
      richText: true,
      lists: true,
      blockquote: true,
      links: true,
      images: true,
      footnotes: false,
      citations: false,
      pullQuotes: true,
      tables: false,
      dateline: true,
      sourceAttribution: true,
    },
    templateFields: {
      ...ALL_HIDDEN,
      headline: 'required',
      subtitle: 'optional',
      byline: 'required',
      body: 'required',
      heroImage: 'optional',
      tags: 'optional',
      category: 'required',
      dateline: 'required',
      lede: 'required',
      sources: 'required',
    },
    minWordCount: 200,
    maxWordCount: 5000,
    descriptionKey: 'content:templates.professional_news.description',
    supportsPillarLinks: true,
    allowedRelationships: ['reference', 'related'],
  },

  /* ──────────────────────────────────────────────────────────────────
   *  Scholarly Article — Academic structure with bibliography.
   *  Trust: High Trust | Review: Scholarly
   *  Spec §3.3: "Abstract, methodology, results, discussion,
   *  bibliography."
   * ────────────────────────────────────────────────────────────────── */
  scholarly_article: {
    key: 'scholarly_article',
    i18nPrefix: 'content:templates.scholarly_article',
    trustTier: 'high_trust',
    reviewModel: 'scholarly',
    bookshelf: {
      spineColorToken: 'var(--content-spine-scholarly)',
      icon: '🎓',
      spineLabel: 'SCHOLARLY',
    },
    editorFeatures: {
      richText: true,
      lists: true,
      blockquote: true,
      links: true,
      images: true,
      footnotes: true,
      citations: true,
      pullQuotes: false,
      tables: true,
      dateline: false,
      sourceAttribution: false,
    },
    templateFields: {
      ...ALL_HIDDEN,
      headline: 'required',
      subtitle: 'optional',
      byline: 'required',
      body: 'required',
      heroImage: 'optional',
      tags: 'optional',
      category: 'required',
      abstract: 'required',
      keywords: 'required',
      bibliography: 'required',
      footnotes: 'required',
      methodology: 'optional',
      acknowledgments: 'optional',
    },
    minWordCount: 1000,
    maxWordCount: 0,
    descriptionKey: 'content:templates.scholarly_article.description',
    supportsPillarLinks: true,
    allowedRelationships: ['reference', 'follow_up', 'related'],
  },
};

// ═══════════════════════════════════════════════════════════════════════
// Ordered Template List (for bookshelf display — low trust → high trust)
// ═══════════════════════════════════════════════════════════════════════

/** Templates in bookshelf display order: left (lowest trust) → right (highest trust). */
export const CONTENT_TEMPLATES_ORDERED: ContentTemplateDefinition[] = [
  CONTENT_TEMPLATES.journal_entry,
  CONTENT_TEMPLATES.topic_tracker,
  CONTENT_TEMPLATES.how_to,
  CONTENT_TEMPLATES.mini_project,
  CONTENT_TEMPLATES.project_chronicle,
  CONTENT_TEMPLATES.lesson_learned,
  CONTENT_TEMPLATES.best_practice,
  CONTENT_TEMPLATES.professional_news,
  CONTENT_TEMPLATES.scholarly_article,
];

// ═══════════════════════════════════════════════════════════════════════
// Flat Lookups
// ═══════════════════════════════════════════════════════════════════════

/** Quick lookup by template key. */
export const CONTENT_TEMPLATE_MAP: Record<string, ContentTemplateDefinition> =
  Object.fromEntries(
    CONTENT_TEMPLATES_ORDERED.map((tmpl) => [tmpl.key, tmpl])
  );

// ═══════════════════════════════════════════════════════════════════════
// Legacy Key Mapping
// ═══════════════════════════════════════════════════════════════════════

/**
 * Maps legacy keys from both the Chronicle and Blog systems to unified
 * content template keys.
 *
 * Chronicle legacy keys (from chronicleTypes.ts LEGACY_CATEGORY_MAP):
 *   success_story, case_study → project_chronicle
 *   reflection → project_chronicle (was retrospective, now chronicle)
 *   initiative → project_chronicle
 *   project → project_chronicle
 *   innovation → project_chronicle (creative project documentation)
 *   learning → lesson_learned
 *   retrospective → lesson_learned
 *   travel → project_chronicle (travel as documented experience)
 *   event → project_chronicle (event as documented experience)
 *
 * Blog legacy keys (from blogTypes.ts):
 *   professional_news → professional_news (unchanged)
 *   scholarly → scholarly_article (key renamed)
 *   how_to → how_to (unchanged)
 *   best_practice → best_practice (unchanged)
 *   lesson_learned → lesson_learned (unchanged)
 *   opinion → (Blog — not a content template)
 *   personal → (Blog — not a content template)
 */
export const LEGACY_KEY_MAP: Record<string, ContentTemplateKey> = {
  // Chronicle legacy keys
  success_story: 'project_chronicle',
  case_study: 'project_chronicle',
  reflection: 'project_chronicle',
  initiative: 'project_chronicle',
  project: 'project_chronicle',
  innovation: 'project_chronicle',
  learning: 'lesson_learned',
  retrospective: 'lesson_learned',
  travel: 'project_chronicle',
  event: 'project_chronicle',
  topic_tracker: 'topic_tracker',
  how_to: 'how_to',

  // Blog legacy keys
  professional_news: 'professional_news',
  scholarly: 'scholarly_article',
  best_practice: 'best_practice',
  lesson_learned: 'lesson_learned',

  // Personal-expression keys — SUBSUMED into journal_entry (DEV-1381, SPEC §12.1).
  // These used to route to 'blog' (a non-template concept backed by localStorage).
  // journal_entry is that concept's proper, server-persisted realization; there is
  // no parallel "Blog" type any more. User-facing vocabulary stays "Blog".
  opinion: 'journal_entry',
  personal: 'journal_entry',
};

// ═══════════════════════════════════════════════════════════════════════
// Helper Functions
// ═══════════════════════════════════════════════════════════════════════

/**
 * Resolve a legacy key (from either Chronicle or Blog system) to a
 * ContentTemplateDefinition. Returns undefined if the key is unrecognised.
 *
 * DEV-1381: every legacy key now resolves to a real template — `opinion` and
 * `personal` map to `journal_entry` rather than to the old non-template 'blog'
 * sentinel. The companion `isBlogLegacyKey()` helper was removed with it (it
 * had no callers and would now always return false).
 */
export function resolveTemplate(
  legacyOrCurrentKey: string
): ContentTemplateDefinition | undefined {
  // Direct match first
  const direct = CONTENT_TEMPLATE_MAP[legacyOrCurrentKey];
  if (direct) return direct;

  // Legacy mapping
  const mapped = LEGACY_KEY_MAP[legacyOrCurrentKey];
  if (mapped) {
    return CONTENT_TEMPLATE_MAP[mapped];
  }

  return undefined;
}

/** Get the localized display name for a content template. */
export function localizedTemplateName(key: ContentTemplateKey): string {
  return t(`content:templates.${key}.name`);
}

/** Get the localized description for a content template. */
export function localizedTemplateDescription(key: ContentTemplateKey): string {
  return t(`content:templates.${key}.description`);
}

/** Get all required field keys for a template. */
export function getRequiredFields(
  key: ContentTemplateKey
): (keyof TemplateFields)[] {
  const def = CONTENT_TEMPLATES[key];
  return (
    Object.entries(def.templateFields) as [keyof TemplateFields, FieldRequirement][]
  )
    .filter(([, req]) => req === 'required')
    .map(([field]) => field);
}

/** Get all visible (required + optional) field keys for a template. */
export function getVisibleFields(
  key: ContentTemplateKey
): (keyof TemplateFields)[] {
  const def = CONTENT_TEMPLATES[key];
  return (
    Object.entries(def.templateFields) as [keyof TemplateFields, FieldRequirement][]
  )
    .filter(([, req]) => req !== 'hidden')
    .map(([field]) => field);
}

/** Get templates available to a user at a given trust tier. */
export function getTemplatesForTier(
  userTier: TrustTier
): ContentTemplateDefinition[] {
  const userLevel = TRUST_TIER_ORDER[userTier];
  return CONTENT_TEMPLATES_ORDERED.filter(
    (tmpl) => TRUST_TIER_ORDER[tmpl.trustTier] <= userLevel
  );
}

/** Get templates the user has NOT yet unlocked (for locked bookshelf silhouettes). */
export function getLockedTemplates(
  userTier: TrustTier
): ContentTemplateDefinition[] {
  const userLevel = TRUST_TIER_ORDER[userTier];
  return CONTENT_TEMPLATES_ORDERED.filter(
    (tmpl) => TRUST_TIER_ORDER[tmpl.trustTier] > userLevel
  );
}

/**
 * Check whether a section is visible for a given editor layout.
 * Falls back to visible if the section has no override.
 */
export function isSectionVisible(
  layout: EditorLayout | undefined,
  sectionKey: string
): boolean {
  if (!layout?.sections) return true;
  const cfg = layout.sections[sectionKey];
  if (!cfg) return true;
  return cfg.visibility !== 'hidden';
}

/**
 * Get the i18n label key for a section, with fallback.
 */
export function getSectionLabelKey(
  layout: EditorLayout | undefined,
  sectionKey: string,
  defaultKey: string
): string {
  return layout?.sections?.[sectionKey]?.labelKey ?? defaultKey;
}

/**
 * Get the i18n hint key for a section, or undefined.
 */
export function getSectionHintKey(
  layout: EditorLayout | undefined,
  sectionKey: string
): string | undefined {
  return layout?.sections?.[sectionKey]?.hintKey;
}

/**
 * Check whether a template uses a multi-tab editor layout.
 * Returns true for Project Chronicle and Topic Tracker.
 */
export function hasMultiTabEditor(key: ContentTemplateKey): boolean {
  return CONTENT_TEMPLATES[key].editorLayout?.tabs !== undefined;
}
